import React from 'react';
import { Navigate, Outlet, useLocation, useNavigate, matchPath } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionsContext';
import Error404 from '../components/Errors/Error404';
import { useSessionExpiration } from '../hooks/useSessionExpiration';
import ExpiredSessionModal from '../components/Expired/ExpiredSessionModal';
import { canAccessRoute } from '../utils/routeAccess';
import Loading from '../components/Loading';
import { ROUTES } from './routePaths';

const adminLoadingText: Record<string, string> = {
    [ROUTES.productos]: 'Cargando productos...',
    [ROUTES.categoriasProductos]: 'Cargando categorías de productos...',
    [ROUTES.dashboard]: 'Cargando dashboard…',
};

const ProtectedRoute = ({ layout: Layout }: { layout?: React.ComponentType<React.PropsWithChildren> }) => {
    const { user, isAuthenticated, loading: authLoading, logout } = useAuth();
    const { permissions, disabledPaths, loading: permissionsLoading } = usePermissions();
    const location = useLocation();
    const navigate = useNavigate();
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();

    const handleCloseExpiredModal = () => {
        setIsSessionExpired(false);
        logout(); 
        navigate('/', { replace: true });
    };

    if (!authLoading && !isAuthenticated) {
        return <Navigate to="/" state={{ from: location }} replace />;
    }

    if (authLoading || permissionsLoading) {
        if (Layout) {
            const route = Object.keys(adminLoadingText).find(path => matchPath(path, location.pathname));
            return <Layout><Loading text={route ? adminLoadingText[route] : 'Cargando…'} /></Layout>;
        }
        const isClientRoute = [
            ROUTES.reservaYa, ROUTES.misCitas, ROUTES.solicitudesReserva,
            ROUTES.perfil, ROUTES.miDashboard, ROUTES.parati, ROUTES.miCorrespondencia,
        ].some(path => matchPath({ path, end: false }, location.pathname));
        return <Loading text="Cargando..." fullScreen={!isClientRoute} />;
    }

    if (!canAccessRoute(location.pathname, user?.role, permissions, disabledPaths)) return <Error404 />;

    const content = (
        <>
            <ExpiredSessionModal
                isOpen={isSessionExpired}
                onClose={handleCloseExpiredModal}
            />
            <Outlet />
        </>
    );
    return Layout ? <Layout>{content}</Layout> : content;
};

export default ProtectedRoute;


